// „Настани" от Маркиране на резервация с няколко маси: „Една сметка" (по
// подразбиране) или „Отделна сметка за всяка маса" — чиста логика без React
// (тестове: orderingSeatBills.test.ts). Огледало на BE
// `service_seat_from_ordering.other_tables_plan`:
//
// - главната сметка е изборът на сервитьора от въпроса „Настани Иванови?"
//   (seatOptions.ts) — своя/видима чужда отворена или нова своя маса;
// - всяка ДРУГА маса: без отворена сметка → нова на сервитьора на главната
//   (своя/нова — на натисналия; чужда — на нейния сервитьор: никога една маса
//   негова, друга моя); с една → закачане, но само ако сервитьорът я вижда
//   (иначе спряно „Маса 2 е заета от Мария"); с няколко → тази на
//   сервитьора на главната, а без такава — спряно;
// - локален режим срещу касата — спряно (касата отваря една сметка).
//
// Масите и сметките по тях са общите с модул „Резервации" (seatBills.ts).

import type { OpenTable, SeatableReservation, SeatableReservations } from "../../../../../api/reservations";
import { seatCheckOf, type SeatHint } from "../../../../reservations/logic/reservationActions";
import { billsOnTableOf, distinctTablesOf, type TableSeatBill } from "../../../../reservations/logic/seatBills";
import { tableNameKey } from "../../../../reservations/logic/tableStatus";
import type { SeatOption } from "./seatOptions";

export interface OrderingSeatBills {
  // Различните маси на резервацията (поне две).
  tableCount: number;
  // Масата на сметката при „Една сметка" — масата на избраната цел; null —
  // целта още не е избрана (няколко възможности, нищо не е избрано).
  mainTable: string | null;
  // „Отделна сметка за всяка маса": главната (целта) първа, после другите
  // маси по реда на резервацията; [] докато целта не е избрана.
  separate: TableSeatBill[];
  // Защо „Отделна…" не може (null — може).
  separateHint: SeatHint | null;
}

const LOCAL_MODE_HINT: SeatHint = { key: "reservations.seatBills.separateLocalMode", params: {} };

/** Отворените сметки по масите, които не са на друга резервация — кандидатите за закачане. */
const freeBillsOf = (reservation: SeatableReservation, data: SeatableReservations): OpenTable[] =>
  seatCheckOf(reservation, [...data.seatedNow, ...data.reservations], data.openTables).freeBills;

/** Сметката на друга маса при „Отделна…": единствената или — от няколко — тази на сервитьора на главната. */
interface OtherTable {
  tableName: string;
  bill: OpenTable | null;
  // Няколко сметки и нито една на сервитьора на главната.
  unresolved: boolean;
}

const otherTableOf = (tableName: string, freeBills: OpenTable[], mainWaiterId: number | null): OtherTable => {
  const onTable = billsOnTableOf(tableName, freeBills);
  const candidates = onTable.length > 1 ? onTable.filter((bill) => bill.waiterId === mainWaiterId) : onTable;
  return {
    tableName,
    bill: candidates.length === 1 ? candidates[0] : null,
    unresolved: onTable.length > 1 && candidates.length !== 1,
  };
};

/** Защо друга маса спира „Отделна…" — първо неразрешима (като BE), после невидима сметка. */
const otherTablesHint = (others: OtherTable[], target: SeatOption, viewerId: number): SeatHint | null => {
  const unresolved = others.find((entry) => entry.unresolved);
  if (unresolved) {
    return target.kind === "new" || target.bill.waiterId === viewerId
      ? { key: "reservations.seatBills.noOwnBill", params: { table: unresolved.tableName } }
      : {
          key: "reservations.seatBills.noBillOfWaiter",
          params: { table: unresolved.tableName, waiter: target.bill.waiterName ?? "" },
        };
  }
  const hidden = others.find((entry) => entry.bill?.hidden === true);
  if (!hidden?.bill) return null;
  const { waiterName } = hidden.bill;
  return waiterName
    ? { key: "seatReservation.takenBy", params: { table: hidden.tableName, waiter: waiterName } }
    : { key: "seatReservation.takenByOther", params: { table: hidden.tableName } };
};

/**
 * Изборът на сметките — само за резервация с поне две различни маси; иначе
 * null и въпросът е както досега. `target` — избраната цел от въпроса (null —
 * още не е избрана). Смята се наново при всеки рендер от пресните данни.
 */
export const orderingSeatBillsOf = (
  reservation: SeatableReservation,
  data: SeatableReservations,
  target: SeatOption | null,
  isLocalMode: boolean,
): OrderingSeatBills | null => {
  const tables = distinctTablesOf(reservation.tableNames);
  if (tables.length < 2) return null;
  if (target === null) {
    const separateHint = isLocalMode ? LOCAL_MODE_HINT : null;
    return { tableCount: tables.length, mainTable: null, separate: [], separateHint };
  }

  const mainKey = tableNameKey(target.kind === "new" ? target.table : target.bill.tableName);
  // Името от резервацията (сметката може да е „2*" от офлайн replay).
  const mainTable = tables.find((name) => tableNameKey(name) === mainKey) ?? tables[0];
  const freeBills = freeBillsOf(reservation, data);
  const mainWaiterId = target.kind === "new" ? data.viewerId : target.bill.waiterId;
  const others = tables
    .filter((name) => tableNameKey(name) !== mainKey)
    .map((tableName) => otherTableOf(tableName, freeBills, mainWaiterId));

  return {
    tableCount: tables.length,
    mainTable,
    separate: [
      { tableName: mainTable, bill: target.kind === "new" ? null : target.bill },
      ...others.map(({ tableName, bill }) => ({ tableName, bill })),
    ],
    separateHint: isLocalMode ? LOCAL_MODE_HINT : otherTablesHint(others, target, data.viewerId),
  };
};

/** Може ли „Настани" с този избор: целта е избрана и „Отделна…" (ако е избрана) не е спряна. */
export const canSeatWithBills = (
  target: SeatOption | null,
  bills: OrderingSeatBills | null,
  separateBills: boolean,
): boolean => target !== null && !(separateBills && bills !== null && bills.separateHint !== null);
